import { Stars } from "@mui/icons-material";
import { Box, Avatar, Typography, IconButton, Rating } from "@mui/material";
import {
  PhoneIcon,
  VideoIcon,
  UserIcon,
  MoreHorizontalIcon,
  MessageSquareIcon,
  MailIcon,
  SendIcon,
  InstagramIcon,
  FacebookIcon,
} from "lucide-react";
import React from "react";

import MessageComposer from "./MessageComposer";

interface Contact {
  avatar: string;
  name: string;
}

interface Message {
  text: string;
  time: string;
  isOutgoing?: boolean;
}

interface ConversationType {
  channel: ChannelType;
  messages: Message[];
}

export type ChannelType = "whatsapp" | "email" | "telegram" | "instagram" | "facebook";

interface ConversationProps {
  conversation: ConversationType | null;
  contact: Contact | null;
  toggleContactInfo: () => void;
  addMessage: (message: string) => void;
  conversationId: string;
  recipient: string;
  stars: number | null;
}

const Conversation: React.FC<ConversationProps> = ({
  conversation,
  contact,
  toggleContactInfo,
  addMessage,
  conversationId,
  recipient,
  stars,
}) => {
  if (!conversation || !contact) return null;

  const getChannelIcon = (channel: ChannelType) => {
    switch (channel) {
      case "whatsapp":
        return <MessageSquareIcon size={16} style={{ color: "#10B981" }} />;
      case "email":
        return <MailIcon size={16} style={{ color: "#3B82F6" }} />;
      case "telegram":
        return <SendIcon size={16} style={{ color: "#60A5FA" }} />;
      case "instagram":
        return <InstagramIcon size={16} style={{ color: "#8B5CF6" }} />;
      case "facebook":
        return <FacebookIcon size={16} style={{ color: "#8B5CF6" }} />;
      default:
        return null;
    }
  };

  const getChannelName = (channel: ChannelType) => {
    switch (channel) {
      case "whatsapp":
        return "WhatsApp";
      case "email":
        return "Email";
      case "telegram":
        return "Telegram";
      case "instagram":
        return "Instagram DM";
      case "facebook":
        return "Facebook Messenger";
      default:
        return channel;
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%", // Full width
        backgroundColor: "#F9FAFB",
        animation: "fadeIn 0.3s",
        overflow: "hidden", // Prevent overall overflow
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          p: { xs: 1, sm: 1.5, md: 2 }, // Responsive padding
          borderBottom: "1px solid #E5E7EB",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0, // Prevent header from shrinking
        }}
      >
        {/* Contact Info - Made more responsive */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            maxWidth: { xs: "60%", sm: "70%", md: "80%" }, // Allow more space for contact info
            overflow: "hidden", // Prevent overflow
          }}
        >
          <Box sx={{ position: "relative", flexShrink: 0 }}>
            {" "}
            {/* Prevent avatar from shrinking */}
            <Avatar
              src={contact.avatar}
              alt={contact.name}
              sx={{
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                objectFit: "cover",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                backgroundColor: "#FFFFFF",
                borderRadius: "50%",
                p: 0.5,
              }}
            >
              {getChannelIcon(conversation.channel)}
            </Box>
          </Box>
          <Box sx={{ ml: 1.5, overflow: "hidden" }}>
            {" "}
            {/* Handle text overflow */}
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 500,
                fontSize: { xs: "0.9rem", sm: "1rem" },
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {contact.name}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "0.75rem",
                color: "#6B7280",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mr: 1,
                }}
              >
                {getChannelName(conversation.channel)}
              </Typography>

              {/* Star rating display - only shown when stars is not null */}
              {stars !== null && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    ml: 0.5,
                    backgroundColor: "#F3F4F6",
                    borderRadius: "12px",
                    px: 1,
                    py: 0.25,
                  }}
                >
                  <Rating
                    value={stars}
                    precision={0.5}
                    readOnly
                    size="small"
                    sx={{
                      color: "#F59E0B",
                      fontSize: "0.75rem",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 0.5,
                      fontWeight: 600,
                      color: "#4B5563",
                    }}
                  >
                    {stars.toFixed(1)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        {/* Action Buttons - Made more responsive */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "4px", sm: "8px", md: "12px" },
            flexShrink: 0, // Prevent buttons from shrinking
          }}
        >
          <IconButton
            sx={{
              color: "#6B7280",
              p: { xs: 0.5, sm: 0.75, md: 1 },
              "&:hover": { color: "#374151", backgroundColor: "#F3F4F6" },
            }}
          >
            <PhoneIcon size={18} />
          </IconButton>
          <IconButton
            sx={{
              color: "#6B7280",
              p: { xs: 0.5, sm: 0.75, md: 1 },
              "&:hover": { color: "#374151", backgroundColor: "#F3F4F6" },
              display: { xs: "none", sm: "flex" }, // Hide on very small screens
            }}
          >
            <VideoIcon size={18} />
          </IconButton>
          <IconButton
            onClick={toggleContactInfo}
            sx={{
              color: "#6B7280",
              p: { xs: 0.5, sm: 0.75, md: 1 },
              "&:hover": { color: "#374151", backgroundColor: "#F3F4F6" },
            }}
          >
            <UserIcon size={18} />
          </IconButton>
          <IconButton
            sx={{
              color: "#6B7280",
              p: { xs: 0.5, sm: 0.75, md: 1 },
              "&:hover": { color: "#374151", backgroundColor: "#F3F4F6" },
            }}
          >
            <MoreHorizontalIcon size={18} />
          </IconButton>
        </Box>
      </Box>
      {/* Messages - Improved overflow handling */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden", // Prevent horizontal overflow
          p: { xs: 1, sm: 1.5, md: 2 }, // Responsive padding
        }}
      >
        {conversation.messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              maxWidth: { xs: "85%", sm: "80%" }, // Slightly wider on mobile
              ml: message.isOutgoing ? "auto" : 0,
              mr: message.isOutgoing ? 0 : "auto",
              animation: "messageIn 0.3s",
            }}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <Box
              sx={{
                p: { xs: 1.5, sm: 2 }, // Smaller padding on mobile
                borderRadius: 2,
                backgroundColor: message.isOutgoing ? "#3B82F6" : "#FFFFFF",
                color: message.isOutgoing ? "#FFFFFF" : "#111827",
                borderBottomRightRadius: message.isOutgoing ? 0 : 8,
                borderBottomLeftRadius: message.isOutgoing ? 8 : 0,
                border: message.isOutgoing ? "none" : "1px solid #E5E7EB",
                wordBreak: "break-word", // Allow text to wrap
                overflowWrap: "break-word", // Handle long words
              }}
            >
              {message.text}
            </Box>
            <Box
              sx={{
                fontSize: "0.75rem",
                mt: 0.5,
                color: "#6B7280",
                display: "flex",
                justifyContent: message.isOutgoing ? "flex-end" : "flex-start",
              }}
            >
              {message.time}
            </Box>
          </Box>
        ))}
      </Box>
      {/* QuickReply and MessageComposer */}
      {/*<QuickReply channel={conversation.channel} />*/}
      <MessageComposer
        channel={conversation.channel}
        addMessage={addMessage}
        conversationId={conversationId}
        recipient={recipient}
      />
    </Box>
  );
};

export default Conversation;
